import { configureApiClient } from "@pontimed/api-client";

import {
  adoptNativeReceipts,
  configureNativeSession,
  nativeAccessToken,
  nativeReceiptHeaders,
  type NativeSessionStore,
} from "./native-session";
import { refreshSession } from "./refresh";

/**
 * App-boot wiring: pass apps/api base URL once, the auth-client wires the
 * 401 → refresh → retry handshake into @pontimed/api-client. Call this
 * before rendering anything that depends on session state.
 */
export interface ConfigureAuthClientOptions {
  apiBaseUrl: string;
  /**
   * The native app's token store (./native-session.ts). Set only by the
   * Capacitor build, which cannot hold the session cookies: the access token
   * then rides as `Authorization: Bearer` (unless `getBearerToken` is given),
   * refresh / sign-out send the refresh token in the body, and the step-up
   * receipts handed in any answer go back as `X-PM-2FA` / `X-PM-Vault`. The
   * web build leaves it undefined and relies on HttpOnly cookies.
   */
  nativeSession?: NativeSessionStore;
  /**
   * Resolves the access token sent as `Authorization: Bearer`. Defaults to
   * the native store's when `nativeSession` is set; the web build leaves both
   * undefined.
   */
  getBearerToken?: () => string | null | Promise<string | null>;
  onAuthFailure?: () => void;
}

export function configureAuthClient(opts: ConfigureAuthClientOptions): void {
  configureNativeSession(opts.nativeSession ?? null);
  configureApiClient({
    baseUrl: opts.apiBaseUrl,
    getBearerToken: opts.getBearerToken ?? (opts.nativeSession ? nativeAccessToken : undefined),
    getRequestHeaders: opts.nativeSession ? nativeReceiptHeaders : undefined,
    onResponseBody: opts.nativeSession ? adoptNativeReceipts : undefined,
    refreshAccessToken: refreshSession,
    onAuthFailure: opts.onAuthFailure,
  });
}
